import { styled } from 'stitches.config'

const Button = styled('button', {
  outline: 'none',
  fontWeight: 700,
  fontSize: 16,
  fontFamily: '$button',
  transition:
    'transform 180ms ease, box-shadow 220ms ease, background-color 220ms ease, filter 220ms ease',
  gap: '$space$2',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: '20px',
  cursor: 'pointer',
  $$focusColor: '$colors$gray12',
  $$shadowColor: '$colors$panelShadow',
  // Glossy raised feel: soft drop shadow + top highlight sheen + sweep
  position: 'relative',
  overflow: 'hidden',
  boxShadow:
    '0 1px 2px 0 $$shadowColor, inset 0 1px 0 0 rgba(255,255,255,0.18)',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '45%',
    left: '-60%',
    background:
      'linear-gradient(105deg, transparent, rgba(255,255,255,0.35), transparent)',
    transform: 'skewX(-18deg)',
    transition: 'left 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
    pointerEvents: 'none',
  },
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow:
      '0 8px 20px -6px $$shadowColor, inset 0 1px 0 0 rgba(255,255,255,0.22)',
    filter: 'brightness(1.05)',
  },
  '&:hover::after': {
    left: '130%',
  },
  '&:active': {
    transform: 'translateY(0)',
    boxShadow:
      '0 1px 2px 0 $$shadowColor, inset 0 2px 6px 0 rgba(0,0,0,0.14)',
    filter: 'brightness(0.97)',
  },
  '&:focus-visible': {
    boxShadow:
      '0 1px 2px 0 $$shadowColor, inset 0 1px 0 0 rgba(255,255,255,0.18), 0 0 0 2px $$focusColor',
  },
  '&:disabled': {
    backgroundColor: '$gray8',
    backgroundImage: 'none',
    color: '$gray11',
    boxShadow: 'none',
    transform: 'none',
    filter: 'none',
    cursor: 'not-allowed',
  },
  '&:disabled:hover': {
    backgroundColor: '$gray8',
    backgroundImage: 'none',
    color: '$gray11',
    boxShadow: 'none',
    transform: 'none',
    filter: 'none',
  },
  variants: {
    color: {
      primary: {
        backgroundColor: '$primary9',
        // Glossy top-light gradient over the base color
        backgroundImage:
          'linear-gradient(180deg, rgba(255,255,255,0.22), rgba(255,255,255,0) 55%)',
        color: 'white',
        '&:hover': {
          backgroundColor: '$primary10',
        },
      },
      secondary: {
        backgroundColor: '$secondary4',
        backgroundImage:
          'linear-gradient(180deg, rgba(255,255,255,0.35), rgba(255,255,255,0) 60%)',
        color: '$primary12',
        '&:hover': {
          backgroundColor: '$secondary5',
        },
      },
      gray3: {
        backgroundColor: '$gray3',
        backgroundImage:
          'linear-gradient(180deg, rgba(255,255,255,0.28), rgba(255,255,255,0) 60%)',
        color: '$gray12',
        '&:hover': {
          backgroundColor: '$gray4',
        },
      },
      gray4: {
        backgroundColor: '$gray4',
        backgroundImage:
          'linear-gradient(180deg, rgba(255,255,255,0.24), rgba(255,255,255,0) 60%)',
        color: '$gray12',
        '&:hover': {
          backgroundColor: '$gray5',
        },
      },
      ghost: {
        backgroundColor: 'transparent',
        backgroundImage: 'none',
        boxShadow: 'none',
        p: 0,
        '&:hover': {
          transform: 'none',
          filter: 'none',
          boxShadow: 'none',
          backgroundColor: '$gray4',
        },
        '&:active': {
          transform: 'none',
          filter: 'none',
          boxShadow: 'none',
        },
        '&:focus-visible': {
          boxShadow: '0 0 0 2px $$focusColor',
        },
      },
    },
    corners: {
      square: {
        borderRadius: 0,
      },
      rounded: {
        borderRadius: 8,
      },
      pill: {
        borderRadius: 99999,
      },
      circle: {
        borderRadius: '99999px',
        alignItems: 'center',
        justifyContent: 'center',
      },
    },
    size: {
      none: {},
      xs: {
        p: '$space$3',
        lineHeight: '16px',
        minHeight: 40,
      },
      small: {
        px: '$space$3',
        py: '$space$4',
        lineHeight: '12px',
        minHeight: 44,
      },
      medium: {
        px: '$space$5',
        py: '$space$3',
        minHeight: 44,
      },
      large: {
        px: '$space$5',
        py: '$space$4',
        minHeight: 52,
      },
    },
  },
  compoundVariants: [
    {
      size: 'xs',
      corners: 'circle',
      css: {
        height: 40,
        width: 40,
        p: 0,
      },
    },
    {
      size: 'small',
      corners: 'circle',
      css: {
        height: 44,
        width: 44,
        p: 0,
      },
    },
    {
      size: 'medium',
      corners: 'circle',
      css: {
        height: 44,
        width: 44,
        p: 0,
      },
    },
    {
      size: 'large',
      corners: 'circle',
      css: {
        height: 52,
        width: 52,
        p: 0,
      },
    },
  ],
  defaultVariants: {
    color: 'primary',
    corners: 'rounded',
    size: 'medium',
  },
})

export default Button
